/**
 * Parses the inline attribute syntax shared with `td add`:
 * `project:` `due:` `wait:` `priority:` `notes:` `+tag` `recur:[every:|after:]2w` `until:` `count:`.
 * Anything else is part of the title. Date and interval text is resolved by the core, not here.
 */

export type DateAttribute = { kind: "clear" } | { kind: "text"; text: string };

export type RecurrenceMode = "every" | "after";

export interface RecurrenceAttribute {
  mode: RecurrenceMode;
  intervalText: string;
}

export interface QuickAddDraft {
  title: string;
  project: string | undefined;
  due: DateAttribute | undefined;
  wait: DateAttribute | undefined;
  priority: number | undefined;
  notes: string | undefined;
  tags: string[];
  recurrence: RecurrenceAttribute | undefined;
  until: string | undefined;
  count: number | undefined;
}

export type AttributeKind =
  | "project"
  | "due"
  | "wait"
  | "priority"
  | "notes"
  | "tag"
  | "recurrence"
  | "until"
  | "count";

export interface AttributeChip {
  kind: AttributeKind;
  label: string;
}

export interface QuickAddParse {
  draft: QuickAddDraft;
  chips: AttributeChip[];
  problems: string[];
}

const PRIORITY_PATTERN = /^\d+$/;

export function parseQuickAdd(input: string): QuickAddParse {
  const draft: QuickAddDraft = {
    title: "",
    project: undefined,
    due: undefined,
    wait: undefined,
    priority: undefined,
    notes: undefined,
    tags: [],
    recurrence: undefined,
    until: undefined,
    count: undefined,
  };
  const chips: AttributeChip[] = [];
  const problems: string[] = [];
  const titleWords: string[] = [];

  for (const word of splitWords(input)) {
    const consumed = consumeAttribute(word.text, draft, chips, problems);
    if (!consumed) titleWords.push(word.raw);
  }
  draft.title = titleWords.join(" ");
  if (draft.recurrence === undefined && (draft.until !== undefined || draft.count !== undefined)) {
    problems.push("until: and count: only make sense together with recur:");
  }
  if (draft.until !== undefined && draft.count !== undefined) {
    problems.push("choose either until: or count:, not both");
  }
  return { draft, chips, problems };
}

interface Word {
  /** The word with quotes removed, used to read attributes. */
  text: string;
  /** The word as typed, kept when it turns out to be part of the title. */
  raw: string;
}

function splitWords(input: string): Word[] {
  const words: Word[] = [];
  let text = "";
  let raw = "";
  let insideQuotes = false;
  const flush = () => {
    if (raw !== "") words.push({ text, raw });
    text = "";
    raw = "";
  };
  for (const character of input) {
    if (character === '"') {
      insideQuotes = !insideQuotes;
      raw += character;
    } else if (/\s/.test(character) && !insideQuotes) {
      flush();
    } else {
      text += character;
      raw += character;
    }
  }
  flush();
  return words;
}

function consumeAttribute(
  word: string,
  draft: QuickAddDraft,
  chips: AttributeChip[],
  problems: string[],
): boolean {
  if (word.startsWith("+") && word.length > 1) {
    const tag = word.slice(1);
    if (!draft.tags.includes(tag)) draft.tags.push(tag);
    chips.push({ kind: "tag", label: `+${tag}` });
    return true;
  }
  const separator = word.indexOf(":");
  if (separator < 0) return false;
  const key = word.slice(0, separator);
  const value = word.slice(separator + 1);
  switch (key) {
    case "project":
    case "proj":
      draft.project = value;
      chips.push({ kind: "project", label: value === "" ? "Inbox" : value });
      return true;
    case "due":
      draft.due = dateAttribute(value);
      chips.push({ kind: "due", label: `due ${value === "" ? "none" : value}` });
      return true;
    case "wait":
      draft.wait = dateAttribute(value);
      chips.push({ kind: "wait", label: `wait ${value === "" ? "none" : value}` });
      return true;
    case "notes":
      draft.notes = value;
      chips.push({ kind: "notes", label: "notes" });
      return true;
    case "priority":
    case "pri":
      return consumePriority(value, draft, chips, problems);
    case "recur":
      return consumeRecurrence(value, draft, chips, problems);
    case "until":
      draft.until = value;
      chips.push({ kind: "until", label: `until ${value}` });
      return true;
    case "count":
      return consumeCount(value, draft, chips, problems);
    default:
      return false;
  }
}

function dateAttribute(value: string): DateAttribute {
  const trimmed = value.trim();
  const clears = trimmed === "" || trimmed.toLowerCase() === "none";
  return clears ? { kind: "clear" } : { kind: "text", text: trimmed };
}

function consumePriority(
  value: string,
  draft: QuickAddDraft,
  chips: AttributeChip[],
  problems: string[],
): boolean {
  if (!PRIORITY_PATTERN.test(value)) {
    problems.push(`priority: needs a number from 0 to 100, got "${value}"`);
    return true;
  }
  draft.priority = Number(value);
  chips.push({ kind: "priority", label: `priority ${value}` });
  return true;
}

function consumeCount(
  value: string,
  draft: QuickAddDraft,
  chips: AttributeChip[],
  problems: string[],
): boolean {
  if (!PRIORITY_PATTERN.test(value)) {
    problems.push(`count: needs a number, got "${value}"`);
    return true;
  }
  draft.count = Number(value);
  chips.push({ kind: "count", label: `${value} times` });
  return true;
}

function consumeRecurrence(
  value: string,
  draft: QuickAddDraft,
  chips: AttributeChip[],
  problems: string[],
): boolean {
  if (value.trim().toLowerCase() === "none") {
    problems.push("recur:none only makes sense when modifying");
    return true;
  }
  const [prefix, rest] = splitOnce(value, ":");
  const mode: RecurrenceMode = prefix === "after" ? "after" : "every";
  const intervalText = prefix === "after" || prefix === "every" ? (rest ?? "") : value;
  draft.recurrence = { mode, intervalText };
  chips.push({
    kind: "recurrence",
    label: mode === "after" ? `after ${intervalText}` : `every ${intervalText}`,
  });
  return true;
}

function splitOnce(text: string, separator: string): [string, string | undefined] {
  const index = text.indexOf(separator);
  if (index < 0) return [text, undefined];
  return [text.slice(0, index), text.slice(index + separator.length)];
}
